import ClickableTableCell from 'Components/ClickableTableCell'
import CustomTablePagination from 'Components/CustomTablePagination'
import EnhancedTableHead, { type HeadCell } from 'Components/EnhancedTableHead/EnhancedTableHead'
import NoWrapTableCell from 'Components/NoWrapTableCell'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import type { FetchSchoolListParams, School } from 'Services/SchoolManagement/Features/School/types'
import { ROWS_PER_PAGE_DEFAULT, SORT_ORDER_ASC } from 'Util/constants'
import { showValueOrHyphen } from 'Util/formatters'
import { Paper, Table, TableBody, TableContainer, Typography, useTheme } from '@mui/material'
import { t } from 'ttag'

interface SchoolListProps {
  rows: School[]
  isLoading: boolean
  onRowClick: (id: string) => void
  selectedSchoolId: string | undefined
  params: FetchSchoolListParams
  handleRequestSort: (event: React.MouseEvent<unknown>, property: string) => void
  handleChangePage: (event: unknown, newPage: number) => void
  count: number
  page: number
}

export default function SchoolList({
  rows,
  isLoading,
  onRowClick,
  selectedSchoolId,
  params,
  handleRequestSort,
  handleChangePage,
  count,
  page
}: Readonly<SchoolListProps>) {
  const theme = useTheme()
  const headCells: HeadCell[] = [
    {
      id: 'schoolId',
      numeric: false,
      disablePadding: false,
      label: t`School identifier`,
      sortable: true
    },
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: t`School or school unit`,
      sortable: true
    },
    {
      id: 'schoolCalendar',
      numeric: false,
      disablePadding: false,
      label: t`Calendar`,
      sortable: false
    }
  ]

  const orderPrefix = `${params.sort_order === SORT_ORDER_ASC ? '-' : ''}`
  const orderBy = `${orderPrefix}${params.sort_by}`

  return (
    <WithLoader loading={isLoading} rows={ROWS_PER_PAGE_DEFAULT} height={33}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="small">
            <EnhancedTableHead
              orderBy={orderBy}
              headCells={headCells}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {rows.map(row => {
                return (
                  <StyledTableRow
                    hover
                    tabIndex={-1}
                    key={row.id}
                    selected={row.id === selectedSchoolId}
                  >
                    <ClickableTableCell onClick={() => onRowClick(row.id)}>
                      {row.school_id}
                    </ClickableTableCell>
                    <ClickableTableCell onClick={() => onRowClick(row.id)}>
                      {row.name}
                      <Typography variant="body2" color={theme.palette.text.secondary}>
                        {row.school_unit_id}
                      </Typography>
                    </ClickableTableCell>
                    <NoWrapTableCell
                      title={showValueOrHyphen(row.school_calendar?.name)}
                      component="th"
                      scope="row"
                    >
                      {showValueOrHyphen(row.school_calendar?.name)}
                    </NoWrapTableCell>
                  </StyledTableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination count={count} page={page} onPageChange={handleChangePage} />
      </Paper>
    </WithLoader>
  )
}
